import type { PromotionPiece } from "./engine";

export interface ExerciseSolution {
  readonly from: string;
  readonly to: string;
  readonly promotion?: PromotionPiece;
}

export type ExerciseDifficulty = "debutant" | "avance";

export interface Exercise {
  readonly id: string;
  /** Filtre choisi au lancement des exercices (cf. `showDifficultyModal`). */
  readonly difficulty: ExerciseDifficulty;
  /** Etiquette courte affichee dans le HUD (ex: "Fourchette"). */
  readonly category: string;
  /** Position de depart, coup aux Blancs sauf mention contraire dans le FEN. */
  readonly fen: string;
  /** Consigne du Taverneu, affichee tant que l'exercice n'est pas resolu. */
  readonly prompt: string;
  /** Coup(s) accepte(s) comme solution (plusieurs si plus d'une piece/case atteint le meme but). */
  readonly solutions: readonly ExerciseSolution[];
  readonly successText: string;
  readonly failText: string;
}

/**
 * Exercices pratiques : contrairement a `STRATEGY_TUTORIAL_STEPS` (narration passive,
 * l'utilisateur clique juste Suivant), chaque exercice pose une position FEN autonome
 * ou le joueur doit lui-meme trouver et jouer le coup gagnant — valide contre le vrai
 * moteur de regles, pas une comparaison de texte. Un coup legal qui ne fait pas partie
 * de `solutions` est refuse (le plateau revient a la position de depart) plutot
 * qu'applique, pour permettre de reessayer sans avoir a tout recommencer.
 *
 * Deux contraintes a respecter pour toute nouvelle entree (memes regles que
 * `STRATEGY_TUTORIAL_STEPS`) : les deux rois doivent toujours etre presents, et chaque
 * FEN est verifiee (position legale, coup(s) solution bien legaux, mat effectif pour
 * les exercices "mat en 1", mat force en 2 coups verifie par recherche exhaustive
 * pour "mat en 2") par `src/game/__tests__/exercises.test.ts`.
 */
export const STRATEGY_EXERCISES: readonly Exercise[] = [
  {
    id: "piece-gratuite",
    difficulty: "debutant",
    category: "Piece a prendre",
    fen: "4k3/8/8/r7/8/8/3B4/4K3 w - - 0 1",
    prompt:
      "Pour commencer, un echauffement : la tour noire est sans aucune defense. Trouve le coup qui la capture gratuitement.",
    solutions: [{ from: "d2", to: "a5" }],
    successText: "Voila, l'ami ! Une piece qui traine sans protection, c'est un cadeau — ne le refuse jamais.",
    failText: "Non, non — regarde bien quelle piece noire personne ne defend, et fonce dessus.",
  },
  {
    id: "fourchette-royale",
    difficulty: "debutant",
    category: "Fourchette",
    fen: "4k3/1r6/8/8/2N5/8/8/4K3 w - - 0 1",
    prompt:
      "Le cavalier peut sauter sur une case ou il menace deux pieces a la fois — dont le roi. Trouve ce coup.",
    solutions: [{ from: "c4", to: "d6" }],
    successText: "Fourchette royale ! Echec, et la tour tombera juste apres — le roi ne peut pas etre a deux endroits.",
    failText: "Pas celui-la. Cherche la case ou le cavalier attaquerait le roi ET la tour en meme temps.",
  },
  {
    id: "clouage-exploite",
    difficulty: "debutant",
    category: "Clouage",
    fen: "4k3/8/8/4n3/3P4/8/8/4R1K1 w - - 0 1",
    prompt:
      "Ce cavalier noir est cloue contre son roi par ta tour : il ne peut pas fuir. Profites-en, il ne sera pas defendu.",
    solutions: [{ from: "d4", to: "e5" }],
    successText: "Exactement ! Cloue, il ne pouvait pas s'ecarter — une piece qui ne peut pas bouger finit toujours par tomber.",
    failText: "Reflechis : quelle piece a toi peut capturer ce cavalier qui ne peut pas s'enfuir ?",
  },
  {
    id: "enfilade-colonne",
    difficulty: "debutant",
    category: "Enfilade",
    fen: "4k3/8/8/4q3/8/8/8/3R3K w - - 0 1",
    prompt:
      "Aligne ta tour sur la colonne e : le roi devra bouger, et la dame juste derriere lui n'aura nulle part ou se cacher.",
    solutions: [{ from: "d1", to: "e1" }],
    successText: "Enfilade ! Le roi degage la colonne pour se sauver, et ta tour recolte la dame au coup suivant.",
    failText: "Pas encore. Amene ta tour sur la colonne e, derriere le roi et la dame noirs.",
  },
  {
    id: "decouverte-gain",
    difficulty: "debutant",
    category: "Attaque a la decouverte",
    fen: "k7/8/1p6/8/N7/8/8/R6K w - - 0 1",
    prompt:
      "Ton cavalier bloque ta propre tour sur la colonne a. S'il bouge en capturant ce pion, il degage la colonne — et decouvre l'echec.",
    solutions: [{ from: "a4", to: "b6" }],
    successText: "Double effet ! Un pion de gagne, et ton roque... euh, ta tour met echec par la meme occasion.",
    failText: "Cherche le saut de cavalier qui capture une piece tout en liberant la colonne a pour ta tour.",
  },
  {
    id: "mat-colonne-dos",
    difficulty: "debutant",
    category: "Mat en 1",
    fen: "6k1/5ppp/8/8/8/8/8/R6K w - - 0 1",
    prompt:
      "Les pions noirs bloquent leur propre roi sur sa derniere rangee. Trouve le coup de tour qui l'y enferme, echec et mat.",
    solutions: [{ from: "a1", to: "a8" }],
    successText: "Echec et mat au dos ! Le roi n'a nulle part ou fuir, coince par ses propres pions — un classique a retenir.",
    failText: "Pas mat. Amene ta tour sur la derniere rangee : le roi, prisonnier de ses pions, ne pourra pas s'echapper.",
  },
  {
    id: "mat-dame-coin",
    difficulty: "debutant",
    category: "Mat en 1",
    fen: "7k/8/7K/8/8/8/8/1Q6 w - - 0 1",
    prompt:
      "Le roi noir est acculé au coin. Ta dame peut y mettre fin en un coup, bien soutenue par ton propre roi.",
    solutions: [{ from: "b1", to: "h7" }],
    successText: "Echec et mat, l'ami ! Ta dame tenait toutes les cases de fuite, protegee par ton roi — impossible d'y echapper.",
    failText: "Pas encore mat. La dame doit atterrir juste a cote du roi, sur une case que ton propre roi protege.",
  },
  {
    id: "double-attaque-dame",
    difficulty: "avance",
    category: "Double attaque",
    fen: "4k2r/8/8/8/8/8/8/K3Q3 w - - 0 1",
    prompt:
      "Passons a plus costaud. Ta dame peut atteindre une case d'ou elle menace le roi ET la tour a la fois, sans que rien ne l'arrete. Trouve-la.",
    solutions: [{ from: "e1", to: "e5" }],
    successText: "Double attaque ! Echec sur la colonne e, et en meme temps la diagonale tient la tour h8 — le roi doit bouger, la tour tombera juste apres.",
    failText: "Cherche une case ou ta dame voit a la fois le roi sur sa colonne et la tour sur une diagonale — les deux d'un seul coup.",
  },
  {
    id: "suppression-defense",
    difficulty: "avance",
    category: "Suppression de la defense",
    fen: "3n2k1/8/1b6/8/8/8/8/1R1Q2K1 w - - 0 1",
    prompt:
      "Ta dame guette le cavalier noir sur la colonne d, mais ce fou le defend. Elimine d'abord ce defenseur, gratuitement, et le cavalier sera perdu au coup suivant.",
    solutions: [{ from: "b1", to: "b6" }],
    successText: "Voila la methode, l'ami : le fou tombe sans rien en retour, et il ne defendait plus rien. Ta dame croque le cavalier au prochain coup — deux pieces pour le prix d'une.",
    failText: "Ne prends pas le cavalier tout de suite, il est bien garde. Repere plutot qui le defend, et supprime ce defenseur en premier.",
  },
  {
    id: "mat-escalier",
    difficulty: "avance",
    category: "Mat en 1",
    fen: "k7/7R/8/8/8/8/8/2R2K2 w - - 0 1",
    prompt:
      "Technique classique du mat a l'escalier : une tour tient deja toute la 7e rangee, l'autre n'a plus qu'a monter donner l'echec final sur la 8e. Trouve ce coup de tour.",
    solutions: [{ from: "c1", to: "c8" }],
    successText: "Mat a l'escalier ! Ta premiere tour interdisait toute la 7e rangee, la seconde a livre l'echec depuis loin — le roi noir n'avait nulle part ou fuir. Retiens cette technique, elle sert dans toutes les finales a deux tours.",
    failText: "Pas mat. Ta tour sur h7 verrouille deja toute la rangee du dessous — amene ta seconde tour donner l'echec sur la derniere rangee, loin du roi.",
  },
  {
    id: "mat-etouffe-en-2",
    difficulty: "avance",
    category: "Mat en 2",
    fen: "5r1k/6pp/7N/3Q4/8/8/8/K7 w - - 0 1",
    prompt:
      "Le grand classique, l'ami : le mat etouffe. Sacrifie ta dame sur une case ou seule la tour peut la reprendre — le roi, lui, n'aura pas le droit de la capturer. Trouve ce sacrifice.",
    solutions: [{ from: "d5", to: "g8" }],
    successText: "Exactement ! Le roi ne pouvait pas prendre ta dame (ton cavalier tenait la case), seule la tour le pouvait — et en reprenant, elle a bouche la derniere case de fuite de son propre roi. Cavalier en f7, et c'est mat : le roi, etouffe par ses propres pieces, ne peut plus bouger du tout.",
    failText: "Pas encore. Cherche le sacrifice de dame, protege par ton cavalier, que seule la tour noire pourra reprendre — le roi restera coince derriere ses propres pions.",
  },
];
